import {
  defaultValidationRuleV1,
  isBlankValidation,
  isGrammarV1Validation,
  isLegacyPreserveValidation,
  serializeValidationRuleV1,
} from './cfg-val-001';

export const AUTHORING_BINDING_KINDS = [
  'CASE_SNAPSHOT',
  'CASE_DYNAMIC',
  'CHECKLIST_DYNAMIC',
] as const;
export type AuthoringBindingKind = (typeof AUTHORING_BINDING_KINDS)[number];

export const AUTHORING_FIELD_VALUE_TYPES = [
  'STRING',
  'TEXT',
  'NUMBER',
  'DATE',
  'DATETIME',
  'ENUM',
  'REF_CONTRACTOR',
  'REF_VEHICLE',
  'REF_PERSON',
] as const;
export type AuthoringFieldValueType = (typeof AUTHORING_FIELD_VALUE_TYPES)[number];

export const AUTHORING_OWNER_CODES = ['CONTRACTOR', 'INSPECTOR', 'SYSTEM'] as const;
export type AuthoringOwnerCode = (typeof AUTHORING_OWNER_CODES)[number];

export const AUTHORING_PLACEMENTS = [
  'HEADER',
  'OBJECT_BLOCK',
  'RESULT_BLOCK',
  'SIGNATURE_BLOCK',
] as const;
export type AuthoringPlacement = (typeof AUTHORING_PLACEMENTS)[number];

export const AUTHORING_CAPTURE_MODES = [
  'METADATA_PREFILL',
  'PRESENCE_ONLY',
  'ADMIN_DECIDE',
] as const;
export type AuthoringCaptureMode = (typeof AUTHORING_CAPTURE_MODES)[number];

export const AUTHORING_PREVIEW_VIEWS = ['CONTRACTOR', 'INSPECTOR', 'PDF'] as const;
export type AuthoringPreviewView = (typeof AUTHORING_PREVIEW_VIEWS)[number];

export const REQUIRED_RULE_TYPES = ['ALWAYS', 'WHEN_APPLICABLE', 'OPTIONAL'] as const;
export type RequiredRuleType = (typeof REQUIRED_RULE_TYPES)[number];

export const APPLICABILITY_RULE_TYPES = [
  'ALWAYS',
  'INSPECTOR_DECLARED',
  'FIELD_EQUALS',
  'ONE_OF',
] as const;
export type ApplicabilityRuleType = (typeof APPLICABILITY_RULE_TYPES)[number];

export const ITEM_VALIDATION_VALUE_TYPES = ['TEXT', 'DATE', 'NUMBER', 'BOOLEAN'] as const;
export type ItemValidationValueType = (typeof ITEM_VALIDATION_VALUE_TYPES)[number];

export const ITEM_VALIDATION_REQUIRED_WHEN = ['ALWAYS', 'APPLICABLE', 'NEVER'] as const;
export type ItemValidationRequiredWhen = (typeof ITEM_VALIDATION_REQUIRED_WHEN)[number];

export const STATIC_SNAPSHOT_PATHS = [
  'vehicle.registration_number',
  'vehicle.vin_or_alternative_identifier',
] as const;

/** Explicit master-profile bindings (Package F). Empty string = unbound in the form. */
export const AUTHORING_MASTER_OBJECT_ROLES = [
  '',
  'PRIMARY_CONTRACTOR',
  'PRIMARY_VEHICLE',
] as const;
export type AuthoringMasterObjectRole = (typeof AUTHORING_MASTER_OBJECT_ROLES)[number];

export const AUTHORING_MASTER_ATTRIBUTES = [
  '',
  'CONTRACTOR_NAME',
  'VEHICLE_REGISTRATION_PLATE',
  'VEHICLE_MAKE_MODEL',
  'VEHICLE_YEAR',
  'VEHICLE_VIN',
  'VEHICLE_ALTERNATIVE_IDENTIFIER',
  'VEHICLE_FACTORY_SERIAL_NUMBER',
] as const;
export type AuthoringMasterAttribute = (typeof AUTHORING_MASTER_ATTRIBUTES)[number];

export function masterAttributesForObjectRole(
  role: string,
): readonly Exclude<AuthoringMasterAttribute, ''>[] {
  if (role === 'PRIMARY_CONTRACTOR') {
    return ['CONTRACTOR_NAME'];
  }
  if (role === 'PRIMARY_VEHICLE') {
    return [
      'VEHICLE_REGISTRATION_PLATE',
      'VEHICLE_MAKE_MODEL',
      'VEHICLE_YEAR',
      'VEHICLE_VIN',
      'VEHICLE_ALTERNATIVE_IDENTIFIER',
      'VEHICLE_FACTORY_SERIAL_NUMBER',
    ];
  }
  return [];
}

export function authoringMasterObjectRoleLabel(role: string): string {
  if (role === 'PRIMARY_CONTRACTOR') {
    return 'Подрядчик';
  }
  if (role === 'PRIMARY_VEHICLE') {
    return 'Основное ТС';
  }
  return 'Не использовать для профиля';
}

export function authoringMasterAttributeLabel(attribute: string): string {
  switch (attribute) {
    case 'CONTRACTOR_NAME':
      return 'Наименование подрядчика';
    case 'VEHICLE_REGISTRATION_PLATE':
      return 'Гос. номер';
    case 'VEHICLE_MAKE_MODEL':
      return 'Марка / модель';
    case 'VEHICLE_YEAR':
      return 'Год выпуска';
    case 'VEHICLE_VIN':
      return 'VIN';
    case 'VEHICLE_ALTERNATIVE_IDENTIFIER':
      return 'Альтернативный идентификатор';
    case 'VEHICLE_FACTORY_SERIAL_NUMBER':
      return 'Заводской серийный номер';
    default:
      return 'Выберите атрибут';
  }
}

function parseMasterBindingFormValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  const text = String(value).trim();
  if (text.length === 0 || text === 'NONE') {
    return '';
  }
  return text;
}

export const SNAPSHOT_PATH_FAMILIES = [
  'STATIC',
  'VEHICLE_DATA',
  'CONTRACTOR_DATA',
  'PERSON_FULL_NAME',
  'PERSON_DATA',
] as const;
export type SnapshotPathFamily = (typeof SNAPSHOT_PATH_FAMILIES)[number];

export interface AuthoringRequiredRule {
  version: 1;
  type: RequiredRuleType;
}

export interface AuthoringApplicabilityRule {
  version: 1;
  type: ApplicabilityRuleType;
  field_path?: string;
  value?: string;
  values_text?: string;
}

export interface AuthoringItemValidationField {
  field_key: string;
  value_type: ItemValidationValueType;
  required_when: ItemValidationRequiredWhen;
}

export interface AuthoringItemValidation {
  version: 1;
  type: 'ITEM_VALIDATION';
  fields: AuthoringItemValidationField[];
}

export interface AuthoringFieldForm {
  field_key: string;
  binding_kind: AuthoringBindingKind;
  label: string;
  value_type: AuthoringFieldValueType;
  owner_code: AuthoringOwnerCode;
  required_rule: AuthoringRequiredRule;
  applicability_rule: AuthoringApplicabilityRule;
  validation: Record<string, unknown>;
  placement: AuthoringPlacement;
  /** Draft-local display order; normalized to array index on save. */
  ordinal: number;
  pdf_label: string;
  snapshot_path: string;
  semantic_key: string;
  snapshot_family: SnapshotPathFamily;
  snapshot_data_key: string;
  snapshot_person_id: string;
  master_object_role: string;
  master_attribute: string;
}

export interface AuthoringDocumentMetadataFieldForm {
  field_code: string;
  label: string;
  value_type: AuthoringFieldValueType;
  required: boolean;
  ordinal: number;
  validation: Record<string, unknown>;
}

export interface AuthoringDocumentRequirementForm {
  document_type_id: string;
  capture_mode: AuthoringCaptureMode;
  display_label: string;
  subject_binding: string;
  required_rule: AuthoringRequiredRule;
  applicability_rule: AuthoringApplicabilityRule;
  verification_note: string;
  ordinal: number;
  metadata_fields: AuthoringDocumentMetadataFieldForm[];
}

export interface AuthoringSectionForm {
  section_code: string;
  ordinal: number;
  title: string;
}

export interface AuthoringItemForm {
  section_code: string;
  item_code: string;
  ordinal: number;
  label: string;
  owner_code: AuthoringOwnerCode;
  required_rule: AuthoringRequiredRule;
  applicability_rule: AuthoringApplicabilityRule;
  validation: AuthoringItemValidation;
  normative_reference: string;
  pdf_label: string;
}

export interface AuthoringPresentationForm {
  enabled: boolean;
  layout_title: string;
  pdf_title: string;
  pdf_include_normative_references: boolean;
}

export type ImportProposedDocumentResolution = 'KNOWN_CODE' | 'LIKENESS_ONLY' | string;

export interface ImportProposedDocumentForm {
  /** Stable UI key for list actions; not persisted as a separate field. */
  client_key: string;
  document_type_code: string | null;
  display_label: string;
  subject_binding: string | null;
  capture_mode: string | null;
  confidence: string;
  resolution: ImportProposedDocumentResolution;
  source_row: number;
  source_locator?: Record<string, unknown>;
  metadata_fields_proposed: AuthoringDocumentMetadataFieldForm[];
}

export interface AuthoringImportEvidenceForm {
  proposed_documents: ImportProposedDocumentForm[];
  unresolved_candidates: Record<string, unknown>[];
  discovery_evidence?: Record<string, unknown>;
}

export interface AuthoringPayloadForm {
  general_name: string;
  general_description: string;
  fields: AuthoringFieldForm[];
  document_requirements: AuthoringDocumentRequirementForm[];
  sections: AuthoringSectionForm[];
  items: AuthoringItemForm[];
  presentation: AuthoringPresentationForm;
  header_binding_review: HeaderBindingReviewForm | null;
  pending_alias_actions: HeaderAliasAction[];
  alias_learning_error: string | null;
  /** Import-only evidence; must round-trip on Save unless explicitly mutated. */
  import_evidence: AuthoringImportEvidenceForm | null;
}

export type HeaderConfidence = 'HIGH' | 'MEDIUM' | 'NONE';
export type HeaderResolutionState = 'UNRESOLVED' | 'BOUND' | 'CREATED_FIELD' | 'IGNORED';

export interface HeaderBindingReviewForm {
  schema: 'header-binding-review/v1';
  source_sheet: string;
  title_candidate: {
    candidate_id: string;
    raw_text: string;
    normalized_text: string;
    source_row: number;
    source_cells: string[];
  } | null;
  groups: Array<{
    candidate_id: string;
    raw_title: string;
    normalized_title: string;
    source_row: number;
    source_cells: string[];
  }>;
  candidates: HeaderFieldCandidateForm[];
}

export interface HeaderFieldCandidateForm {
  candidate_id: string;
  raw_label: string;
  normalized_label: string;
  source_row: number;
  label_cells: string[];
  value_cells: string[];
  group_id: string | null;
  group_title: string | null;
  confidence: HeaderConfidence;
  suggested_binding: Record<string, unknown> | null;
  resolution: {
    state: HeaderResolutionState;
    resulting_field_key?: string;
    reason?: string;
  };
}

export interface HeaderAliasAction {
  normalized_label: string;
  normalized_group: string | null;
  status: 'SUGGESTED' | 'TRUSTED';
  binding_blueprint: Record<string, unknown>;
  raw_label_examples: string[];
}

export function alwaysRequiredRule(): AuthoringRequiredRule {
  return { version: 1, type: 'ALWAYS' };
}

export function alwaysApplicabilityRule(): AuthoringApplicabilityRule {
  return { version: 1, type: 'ALWAYS' };
}

export function emptyItemValidation(): AuthoringItemValidation {
  return { version: 1, type: 'ITEM_VALIDATION', fields: [] };
}

export function emptyAuthoringField(ordinal = 0): AuthoringFieldForm {
  return {
    field_key: `field_${ordinal + 1}`,
    binding_kind: 'CHECKLIST_DYNAMIC',
    label: '',
    value_type: 'STRING',
    owner_code: 'CONTRACTOR',
    required_rule: alwaysRequiredRule(),
    applicability_rule: alwaysApplicabilityRule(),
    validation: { version: 1 },
    placement: 'OBJECT_BLOCK',
    ordinal,
    pdf_label: '',
    snapshot_path: '',
    semantic_key: '',
    snapshot_family: 'STATIC',
    snapshot_data_key: '',
    snapshot_person_id: '',
    master_object_role: '',
    master_attribute: '',
  };
}

/**
 * Allocates a stable CASE_DYNAMIC semantic_key for MANUAL authoring.
 * Convention mirrors import header keys (`hdr.sem.{16 hex}`) with prefix `manual.sem.`.
 * One-shot UUID fragment (not per-render); collision-checked against sibling keys in the draft.
 * Existing non-empty keys (import/clone) must never be replaced by callers.
 */
export function allocateManualCaseDynamicSemanticKey(
  occupiedSemanticKeys: readonly string[] = [],
): string {
  const occupied = new Set(
    occupiedSemanticKeys.map((key) => key.trim()).filter((key) => key.length > 0),
  );
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const suffix = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    const candidate = `manual.sem.${suffix}`;
    if (!occupied.has(candidate)) {
      return candidate;
    }
  }
  return `manual.sem.${crypto.randomUUID().replace(/-/g, '')}`;
}

/**
 * When binding becomes CASE_DYNAMIC and semantic_key is blank, initialize once.
 * Does not rewrite non-empty keys. Leaving CASE_DYNAMIC keeps form semantic_key
 * intact (serialize still nulls it for other bindings).
 */
export function withAuthoringBindingKind(
  field: AuthoringFieldForm,
  bindingKind: AuthoringBindingKind,
  occupiedSemanticKeys: readonly string[] = [],
): AuthoringFieldForm {
  const next: AuthoringFieldForm = { ...field, binding_kind: bindingKind };
  if (bindingKind !== 'CASE_DYNAMIC') {
    return next;
  }
  if (next.semantic_key.trim().length > 0) {
    return next;
  }
  const occupied = occupiedSemanticKeys.filter(
    (key) => key.trim().length > 0 && key.trim() !== next.semantic_key.trim(),
  );
  return {
    ...next,
    semantic_key: allocateManualCaseDynamicSemanticKey(occupied),
  };
}

/** Draft-local field_key unique among siblings (never copy external TemplateVersion keys). */
export function allocateUniqueAuthoringFieldKey(
  existingFields: readonly AuthoringFieldForm[],
  preferredBase = 'field',
): string {
  const occupied = new Set(
    existingFields.map((row) => row.field_key.trim()).filter((key) => key.length > 0),
  );
  let index = existingFields.length + 1;
  let candidate = `${preferredBase}_${index}`;
  while (occupied.has(candidate)) {
    index += 1;
    candidate = `${preferredBase}_${index}`;
  }
  return candidate;
}

/**
 * Force a new CASE_DYNAMIC semantic_key for unlink in the current draft only.
 * Unlike withAuthoringBindingKind, always replaces a non-empty key.
 */
export function forceUnlinkCaseDynamicSemanticKey(
  field: AuthoringFieldForm,
  occupiedSemanticKeys: readonly string[] = [],
): AuthoringFieldForm {
  if (field.binding_kind !== 'CASE_DYNAMIC') {
    return field;
  }
  const occupied = occupiedSemanticKeys
    .map((key) => key.trim())
    .filter((key) => key.length > 0 && key !== field.semantic_key.trim());
  return {
    ...field,
    semantic_key: allocateManualCaseDynamicSemanticKey(occupied),
  };
}

/** New unlinked CASE_DYNAMIC for «Создать новое поле» (new manual.sem.*; never reuse candidates). */
export function createUnlinkedCaseDynamicField(
  label: string,
  existingFields: readonly AuthoringFieldForm[],
): AuthoringFieldForm {
  const base = emptyAuthoringField(existingFields.length);
  const seeded: AuthoringFieldForm = {
    ...base,
    field_key: allocateUniqueAuthoringFieldKey(existingFields),
    label: label.trim(),
  };
  return withAuthoringBindingKind(
    seeded,
    'CASE_DYNAMIC',
    existingFields.map((row) => row.semantic_key),
  );
}

/** Minimal catalog candidate shape for link helpers (avoids circular admin-api import). */
export interface FieldReuseCandidateSource {
  readonly binding_kind: string;
  readonly semantic_key: string;
  readonly label: string;
  readonly value_type: string;
  readonly owner_code: string;
  readonly required_rule: Record<string, unknown>;
  readonly applicability_rule: Record<string, unknown>;
  readonly validation: Record<string, unknown>;
  readonly linkable: boolean;
  readonly conflict_reason_codes?: readonly string[];
}

function stableJsonFingerprint(value: unknown): string {
  return JSON.stringify(normalizeJsonForCompare(value));
}

function normalizeJsonForCompare(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeJsonForCompare(item));
  }
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    const normalized: Record<string, unknown> = {};
    for (const key of keys) {
      normalized[key] = normalizeJsonForCompare(record[key]);
    }
    return normalized;
  }
  return value;
}

function requiredRuleSupported(raw: unknown): boolean {
  const object = asRecord(raw);
  return (
    object !== null &&
    (object.type === 'ALWAYS' || object.type === 'WHEN_APPLICABLE' || object.type === 'OPTIONAL')
  );
}

function applicabilityRuleSupported(raw: unknown): boolean {
  const object = asRecord(raw);
  if (object === null) {
    return false;
  }
  return (
    object.type === 'ALWAYS' ||
    object.type === 'INSPECTOR_DECLARED' ||
    object.type === 'FIELD_EQUALS' ||
    object.type === 'ONE_OF'
  );
}

/**
 * Human-readable block reason for «Добавить и связать», or null if safe to link.
 * Does not invent identity from labels / manual.sem.* prefixes.
 */
export function fieldReuseLinkBlockReason(
  candidate: FieldReuseCandidateSource,
  draftFields: readonly AuthoringFieldForm[],
): string | null {
  if (candidate.binding_kind !== 'CASE_DYNAMIC') {
    return 'Можно связать только общее поле осмотра';
  }
  const semanticKey = candidate.semantic_key.trim();
  if (semanticKey.length < 1) {
    return 'У определения нет общего ключа';
  }
  if (
    draftFields.some(
      (row) =>
        row.binding_kind === 'CASE_DYNAMIC' && row.semantic_key.trim() === semanticKey,
    )
  ) {
    return 'Поле уже добавлено';
  }
  if (!candidate.linkable) {
    const codes = candidate.conflict_reason_codes ?? [];
    if (codes.includes('OWNER_CODE_MISMATCH')) {
      return 'Разный ответственный за заполнение';
    }
    if (codes.includes('VALUE_TYPE_MISMATCH')) {
      return 'Разный тип поля';
    }
    if (
      codes.includes('REQUIRED_RULE_MISMATCH') ||
      codes.includes('APPLICABILITY_RULE_MISMATCH') ||
      codes.includes('VALIDATION_MISMATCH')
    ) {
      return 'Отличаются условия заполнения';
    }
    return 'Определения в других шаблонах не согласованы';
  }
  if (
    !AUTHORING_FIELD_VALUE_TYPES.includes(candidate.value_type as AuthoringFieldValueType)
  ) {
    return 'Разный тип поля';
  }
  if (!AUTHORING_OWNER_CODES.includes(candidate.owner_code as AuthoringOwnerCode)) {
    return 'Разный ответственный за заполнение';
  }
  if (!requiredRuleSupported(candidate.required_rule)) {
    return 'Отличаются условия заполнения';
  }
  if (!applicabilityRuleSupported(candidate.applicability_rule)) {
    return 'Отличаются условия заполнения';
  }
  if (
    candidate.validation === null ||
    typeof candidate.validation !== 'object' ||
    Array.isArray(candidate.validation)
  ) {
    return 'Отличаются условия заполнения';
  }
  return null;
}

/** Build a draft field that reuses an existing CASE_DYNAMIC semantic_key (link). */
export function authoringFieldFromReuseCandidate(
  candidate: FieldReuseCandidateSource,
  existingFields: readonly AuthoringFieldForm[],
): AuthoringFieldForm | { readonly error: string } {
  const block = fieldReuseLinkBlockReason(candidate, existingFields);
  if (block !== null) {
    return { error: block };
  }
  const base = emptyAuthoringField(existingFields.length);
  return {
    ...base,
    field_key: allocateUniqueAuthoringFieldKey(existingFields),
    binding_kind: 'CASE_DYNAMIC',
    label: candidate.label.trim().length > 0 ? candidate.label.trim() : base.label,
    value_type: candidate.value_type as AuthoringFieldValueType,
    owner_code: candidate.owner_code as AuthoringOwnerCode,
    required_rule: parseRequiredRule(candidate.required_rule),
    applicability_rule: parseApplicabilityRule(candidate.applicability_rule),
    validation: deepCloneJson(candidate.validation),
    semantic_key: candidate.semantic_key.trim(),
  };
}

export function fieldReuseIdentityCriticalChanged(
  previous: AuthoringFieldForm,
  next: AuthoringFieldForm,
): boolean {
  return (
    previous.value_type !== next.value_type ||
    previous.owner_code !== next.owner_code ||
    stableJsonFingerprint(serializeRequiredRule(previous.required_rule)) !==
      stableJsonFingerprint(serializeRequiredRule(next.required_rule)) ||
    stableJsonFingerprint(serializeApplicabilityRule(previous.applicability_rule)) !==
      stableJsonFingerprint(serializeApplicabilityRule(next.applicability_rule)) ||
    stableJsonFingerprint(previous.validation) !== stableJsonFingerprint(next.validation)
  );
}

export function emptyDocumentRequirement(ordinal = 0): AuthoringDocumentRequirementForm {
  return {
    document_type_id: '',
    capture_mode: 'METADATA_PREFILL',
    display_label: '',
    subject_binding: 'VEHICLE',
    required_rule: alwaysRequiredRule(),
    applicability_rule: alwaysApplicabilityRule(),
    verification_note: '',
    ordinal,
    metadata_fields: [],
  };
}

export function emptyDocumentMetadataField(ordinal = 0): AuthoringDocumentMetadataFieldForm {
  return {
    field_code: `meta_${ordinal + 1}`,
    label: '',
    value_type: 'STRING',
    required: false,
    ordinal,
    validation: { version: 1 },
  };
}

/**
 * Deterministic authoring COPY of DocumentType.default_metadata_schema.fields
 * (PO-DOCID-004). Shared by auto-bind, bind-existing, and manual type selection.
 */
export function metadataFieldsFromDocumentTypeDefaultSchema(
  schema: Record<string, unknown> | null | undefined,
): AuthoringDocumentMetadataFieldForm[] {
  if (schema === null || schema === undefined || typeof schema !== 'object') {
    return [];
  }
  const rawFields = Array.isArray(schema.fields) ? schema.fields : [];
  const allowed = new Set<string>(AUTHORING_FIELD_VALUE_TYPES);
  const copied: AuthoringDocumentMetadataFieldForm[] = [];

  for (let index = 0; index < rawFields.length; index += 1) {
    const row = rawFields[index];
    if (row === null || typeof row !== 'object' || Array.isArray(row)) {
      continue;
    }
    const record = row as Record<string, unknown>;
    const fieldCode = typeof record.field_code === 'string' ? record.field_code.trim() : '';
    if (fieldCode.length < 1) {
      continue;
    }
    const rawType = typeof record.value_type === 'string' ? record.value_type : 'STRING';
    const valueType = (allowed.has(rawType) ? rawType : 'STRING') as AuthoringFieldValueType;
    const validation =
      record.validation !== null &&
      typeof record.validation === 'object' &&
      !Array.isArray(record.validation)
        ? { ...(record.validation as Record<string, unknown>) }
        : { version: 1 };
    const ordinalRaw = Number(record.ordinal);
    copied.push({
      field_code: fieldCode,
      label:
        typeof record.label === 'string' && record.label.trim().length > 0
          ? record.label.trim()
          : fieldCode,
      value_type: valueType,
      required: record.required === true,
      ordinal: Number.isFinite(ordinalRaw) ? ordinalRaw : index + 1,
      validation,
    });
  }

  return copied
    .slice()
    .sort((a, b) => a.ordinal - b.ordinal || a.field_code.localeCompare(b.field_code));
}

export function emptySection(ordinal = 0): AuthoringSectionForm {
  return {
    section_code: `SEC_${ordinal + 1}`,
    ordinal,
    title: '',
  };
}

export function emptyItem(sectionCode: string, ordinal = 0): AuthoringItemForm {
  return {
    section_code: sectionCode,
    item_code: `ITEM_${ordinal + 1}`,
    ordinal,
    label: '',
    owner_code: 'INSPECTOR',
    required_rule: alwaysRequiredRule(),
    applicability_rule: alwaysApplicabilityRule(),
    validation: emptyItemValidation(),
    normative_reference: '',
    pdf_label: '',
  };
}

export function emptyPresentation(): AuthoringPresentationForm {
  return {
    enabled: false,
    layout_title: '',
    pdf_title: '',
    pdf_include_normative_references: false,
  };
}

export function emptyAuthoringPayloadForm(): AuthoringPayloadForm {
  return {
    general_name: '',
    general_description: '',
    fields: [],
    document_requirements: [],
    sections: [],
    items: [],
    presentation: emptyPresentation(),
    header_binding_review: null,
    pending_alias_actions: [],
    alias_learning_error: null,
    import_evidence: null,
  };
}

export function emptyImportEvidence(): AuthoringImportEvidenceForm {
  return {
    proposed_documents: [],
    unresolved_candidates: [],
  };
}

function proposalClientKey(
  proposal: {
    readonly document_type_code?: string | null;
    readonly display_label?: string;
    readonly source_row?: number;
    readonly resolution?: string;
  },
  index: number,
): string {
  const code = proposal.document_type_code ?? '';
  const label = proposal.display_label ?? '';
  const row = proposal.source_row ?? index;
  const resolution = proposal.resolution ?? '';
  return `${index}:${row}:${resolution}:${code}:${label}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseRequiredRule(value: unknown): AuthoringRequiredRule {
  const object = asRecord(value);
  const type = object?.type;
  if (type === 'ALWAYS' || type === 'WHEN_APPLICABLE' || type === 'OPTIONAL') {
    return { version: 1, type };
  }
  return alwaysRequiredRule();
}

function parseApplicabilityRule(value: unknown): AuthoringApplicabilityRule {
  const object = asRecord(value);
  const type = object?.type;
  if (type === 'ALWAYS' || type === 'INSPECTOR_DECLARED') {
    return { version: 1, type };
  }
  if (type === 'FIELD_EQUALS') {
    return {
      version: 1,
      type,
      field_path: asString(object?.field_path),
      value:
        typeof object?.value === 'string' || typeof object?.value === 'number'
          ? String(object.value)
          : '',
    };
  }
  if (type === 'ONE_OF') {
    const values = Array.isArray(object?.values)
      ? object.values.map((item) => String(item)).join(', ')
      : '';
    return {
      version: 1,
      type,
      field_path: asString(object?.field_path),
      values_text: values,
    };
  }
  return alwaysApplicabilityRule();
}

function parseItemValidation(value: unknown): AuthoringItemValidation {
  const object = asRecord(value);
  if (object?.type !== 'ITEM_VALIDATION' || !Array.isArray(object.fields)) {
    return emptyItemValidation();
  }
  return {
    version: 1,
    type: 'ITEM_VALIDATION',
    fields: object.fields.map((row, index) => {
      const field = asRecord(row) ?? {};
      const valueType = field.value_type;
      const requiredWhen = field.required_when;
      return {
        field_key: asString(field.field_key, `val_${index + 1}`),
        value_type:
          valueType === 'TEXT' ||
          valueType === 'DATE' ||
          valueType === 'NUMBER' ||
          valueType === 'BOOLEAN'
            ? valueType
            : 'TEXT',
        required_when:
          requiredWhen === 'ALWAYS' || requiredWhen === 'APPLICABLE' || requiredWhen === 'NEVER'
            ? requiredWhen
            : 'ALWAYS',
      };
    }),
  };
}

function pdfLabelFromMetadata(value: unknown): string {
  const object = asRecord(value);
  return asString(object?.label);
}

function classifySnapshotPath(
  path: string,
): Pick<
  AuthoringFieldForm,
  'snapshot_family' | 'snapshot_data_key' | 'snapshot_person_id' | 'snapshot_path'
> {
  if ((STATIC_SNAPSHOT_PATHS as readonly string[]).includes(path)) {
    return {
      snapshot_family: 'STATIC',
      snapshot_path: path,
      snapshot_data_key: '',
      snapshot_person_id: '',
    };
  }
  if (path.startsWith('vehicle.data.')) {
    return {
      snapshot_family: 'VEHICLE_DATA',
      snapshot_path: path,
      snapshot_data_key: path.slice('vehicle.data.'.length),
      snapshot_person_id: '',
    };
  }
  if (path.startsWith('contractor.data.')) {
    return {
      snapshot_family: 'CONTRACTOR_DATA',
      snapshot_path: path,
      snapshot_data_key: path.slice('contractor.data.'.length),
      snapshot_person_id: '',
    };
  }
  const personFull = /^person\[([0-9a-fA-F-]{36})\]\.full_name$/.exec(path);
  if (personFull !== null) {
    return {
      snapshot_family: 'PERSON_FULL_NAME',
      snapshot_path: path,
      snapshot_data_key: '',
      snapshot_person_id: personFull[1] ?? '',
    };
  }
  const personData = /^person\[([0-9a-fA-F-]{36})\]\.data\.(.+)$/.exec(path);
  if (personData !== null) {
    return {
      snapshot_family: 'PERSON_DATA',
      snapshot_path: path,
      snapshot_data_key: personData[2] ?? '',
      snapshot_person_id: personData[1] ?? '',
    };
  }
  return {
    snapshot_family: 'STATIC',
    snapshot_path: path,
    snapshot_data_key: '',
    snapshot_person_id: '',
  };
}

export function resolveSnapshotPath(field: AuthoringFieldForm): string | null {
  if (field.binding_kind !== 'CASE_SNAPSHOT') {
    return null;
  }
  switch (field.snapshot_family) {
    case 'STATIC':
      return field.snapshot_path.length > 0 ? field.snapshot_path : null;
    case 'VEHICLE_DATA':
      return field.snapshot_data_key.length > 0 ? `vehicle.data.${field.snapshot_data_key}` : null;
    case 'CONTRACTOR_DATA':
      return field.snapshot_data_key.length > 0
        ? `contractor.data.${field.snapshot_data_key}`
        : null;
    case 'PERSON_FULL_NAME':
      return field.snapshot_person_id.length > 0
        ? `person[${field.snapshot_person_id}].full_name`
        : null;
    case 'PERSON_DATA':
      return field.snapshot_person_id.length > 0 && field.snapshot_data_key.length > 0
        ? `person[${field.snapshot_person_id}].data.${field.snapshot_data_key}`
        : null;
    default:
      return null;
  }
}

function serializeRequiredRule(rule: AuthoringRequiredRule): Record<string, unknown> {
  return { version: 1, type: rule.type };
}

function serializeApplicabilityRule(rule: AuthoringApplicabilityRule): Record<string, unknown> {
  if (rule.type === 'FIELD_EQUALS') {
    return {
      version: 1,
      type: 'FIELD_EQUALS',
      field_path: rule.field_path ?? '',
      value: rule.value ?? '',
    };
  }
  if (rule.type === 'ONE_OF') {
    const values = (rule.values_text ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return {
      version: 1,
      type: 'ONE_OF',
      field_path: rule.field_path ?? '',
      values,
    };
  }
  return { version: 1, type: rule.type };
}

function serializePdfMetadata(label: string): Record<string, unknown> | undefined {
  const trimmed = label.trim();
  return trimmed.length === 0 ? undefined : { label: trimmed };
}

function parseImportProposedDocument(
  value: unknown,
  index: number,
): ImportProposedDocumentForm | null {
  const row = asRecord(value);
  if (row === null) {
    return null;
  }
  const metadataRaw = Array.isArray(row.metadata_fields_proposed)
    ? row.metadata_fields_proposed
    : [];
  const resolution =
    row.resolution === 'KNOWN_CODE' || row.resolution === 'LIKENESS_ONLY'
      ? row.resolution
      : asString(row.resolution, 'LIKENESS_ONLY');
  const codeRaw = row.document_type_code;
  const documentTypeCode =
    typeof codeRaw === 'string' && codeRaw.trim().length > 0 ? codeRaw.trim() : null;
  return {
    client_key: proposalClientKey(
      {
        document_type_code: documentTypeCode,
        display_label: asString(row.display_label),
        source_row: asNumber(row.source_row, index),
        resolution,
      },
      index,
    ),
    document_type_code: documentTypeCode,
    display_label: asString(row.display_label),
    subject_binding:
      row.subject_binding === null || row.subject_binding === undefined
        ? null
        : asString(row.subject_binding) || null,
    capture_mode:
      row.capture_mode === null || row.capture_mode === undefined
        ? null
        : asString(row.capture_mode) || null,
    confidence: asString(row.confidence, 'MEDIUM'),
    resolution,
    source_row: asNumber(row.source_row, index),
    ...(asRecord(row.source_locator) !== null
      ? { source_locator: asRecord(row.source_locator)! }
      : {}),
    metadata_fields_proposed: metadataRaw.map((metaRow, metaIndex) => {
      const meta = asRecord(metaRow) ?? {};
      const metaType = AUTHORING_FIELD_VALUE_TYPES.includes(
        meta.value_type as AuthoringFieldValueType,
      )
        ? (meta.value_type as AuthoringFieldValueType)
        : 'STRING';
      return {
        field_code: asString(meta.field_code, `meta_${metaIndex + 1}`),
        label: asString(meta.label),
        value_type: metaType,
        required: asBoolean(meta.required),
        ordinal: asNumber(meta.ordinal, metaIndex),
        validation: asRecord(meta.validation) ?? { version: 1 },
      };
    }),
  };
}

function parseImportEvidence(value: unknown): AuthoringImportEvidenceForm | null {
  const root = asRecord(value);
  if (root === null) {
    return null;
  }
  const proposedRaw = Array.isArray(root.proposed_documents) ? root.proposed_documents : [];
  const unresolvedRaw = Array.isArray(root.unresolved_candidates) ? root.unresolved_candidates : [];
  const proposed = proposedRaw
    .map((row, index) => parseImportProposedDocument(row, index))
    .filter((row): row is ImportProposedDocumentForm => row !== null);
  const unresolved = unresolvedRaw
    .map((row) => asRecord(row))
    .filter((row): row is Record<string, unknown> => row !== null)
    .map((row) => ({ ...row }));
  const discovery = asRecord(root.discovery_evidence);
  if (proposed.length === 0 && unresolved.length === 0 && discovery === null) {
    return null;
  }
  return {
    proposed_documents: proposed,
    unresolved_candidates: unresolved,
    ...(discovery !== null ? { discovery_evidence: { ...discovery } } : {}),
  };
}

function serializeImportProposedDocument(
  proposal: ImportProposedDocumentForm,
): Record<string, unknown> {
  const row: Record<string, unknown> = {
    document_type_code: proposal.document_type_code,
    display_label: proposal.display_label,
    subject_binding: proposal.subject_binding,
    capture_mode: proposal.capture_mode,
    confidence: proposal.confidence,
    resolution: proposal.resolution,
    source_row: proposal.source_row,
  };
  if (proposal.source_locator !== undefined) {
    row.source_locator = proposal.source_locator;
  }
  if (proposal.metadata_fields_proposed.length > 0) {
    row.metadata_fields_proposed = proposal.metadata_fields_proposed.map((meta, metaIndex) => ({
      field_code: meta.field_code.trim(),
      label: meta.label.trim(),
      value_type: meta.value_type,
      required: meta.required,
      ordinal: meta.ordinal >= 0 ? meta.ordinal : metaIndex,
      validation: serializeCfgValFieldValidation(meta.value_type, meta.validation),
    }));
  }
  return row;
}

function serializeImportEvidence(evidence: AuthoringImportEvidenceForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    proposed_documents: evidence.proposed_documents.map(serializeImportProposedDocument),
    unresolved_candidates: evidence.unresolved_candidates.map((row) => ({ ...row })),
  };
  if (evidence.discovery_evidence !== undefined) {
    payload.discovery_evidence = { ...evidence.discovery_evidence };
  }
  return payload;
}

/**
 * Convert an import proposal into a normal document_requirement and remove that proposal
 * from import_evidence (and matching DOCUMENT_CANDIDATE unresolved row when present).
 * Metadata fields are initialized from the selected DocumentType.default_metadata_schema
 * (authoring COPY), not from importer proposal generics.
 */
export function bindImportProposalToDocumentType(
  form: AuthoringPayloadForm,
  clientKey: string,
  documentTypeId: string,
  options: { readonly defaultMetadataSchema?: Record<string, unknown> | null } = {},
): AuthoringPayloadForm {
  const evidence = form.import_evidence;
  if (evidence === null) {
    return form;
  }
  const index = evidence.proposed_documents.findIndex((row) => row.client_key === clientKey);
  if (index < 0) {
    return form;
  }
  const proposal = evidence.proposed_documents[index]!;
  const capture = AUTHORING_CAPTURE_MODES.includes(proposal.capture_mode as AuthoringCaptureMode)
    ? (proposal.capture_mode as AuthoringCaptureMode)
    : 'METADATA_PREFILL';
  const nextRequirement: AuthoringDocumentRequirementForm = {
    document_type_id: documentTypeId,
    capture_mode: capture,
    display_label: proposal.display_label,
    subject_binding:
      proposal.subject_binding && proposal.subject_binding.trim().length > 0
        ? proposal.subject_binding
        : 'VEHICLE',
    required_rule: alwaysRequiredRule(),
    applicability_rule: alwaysApplicabilityRule(),
    verification_note: '',
    ordinal: form.document_requirements.length,
    metadata_fields: metadataFieldsFromDocumentTypeDefaultSchema(options.defaultMetadataSchema),
  };
  const remainingProposed = evidence.proposed_documents.filter((_, i) => i !== index);
  const remainingUnresolved = evidence.unresolved_candidates.filter((candidate) => {
    if (asString(candidate.kind) !== 'DOCUMENT_CANDIDATE') {
      return true;
    }
    const sameRow = asNumber(candidate.source_row, -1) === proposal.source_row;
    const sameLabel = asString(candidate.label) === proposal.display_label;
    return !(sameRow && sameLabel);
  });
  const nextEvidence: AuthoringImportEvidenceForm | null =
    remainingProposed.length > 0 ||
    remainingUnresolved.length > 0 ||
    evidence.discovery_evidence !== undefined
      ? {
          proposed_documents: remainingProposed,
          unresolved_candidates: remainingUnresolved,
          ...(evidence.discovery_evidence !== undefined
            ? { discovery_evidence: evidence.discovery_evidence }
            : {}),
        }
      : null;
  return {
    ...form,
    document_requirements: reindexOrdinals([...form.document_requirements, nextRequirement]),
    import_evidence: nextEvidence,
  };
}

/** Explicitly exclude one proposal from this draft's import_evidence only. */
export function excludeImportProposal(
  form: AuthoringPayloadForm,
  clientKey: string,
): AuthoringPayloadForm {
  const evidence = form.import_evidence;
  if (evidence === null) {
    return form;
  }
  const index = evidence.proposed_documents.findIndex((row) => row.client_key === clientKey);
  if (index < 0) {
    return form;
  }
  const proposal = evidence.proposed_documents[index]!;
  const remainingProposed = evidence.proposed_documents.filter((_, i) => i !== index);
  const remainingUnresolved = evidence.unresolved_candidates.filter((candidate) => {
    if (asString(candidate.kind) !== 'DOCUMENT_CANDIDATE') {
      return true;
    }
    const sameRow = asNumber(candidate.source_row, -1) === proposal.source_row;
    const sameLabel = asString(candidate.label) === proposal.display_label;
    return !(sameRow && sameLabel);
  });
  const nextEvidence: AuthoringImportEvidenceForm | null =
    remainingProposed.length > 0 ||
    remainingUnresolved.length > 0 ||
    evidence.discovery_evidence !== undefined
      ? {
          proposed_documents: remainingProposed,
          unresolved_candidates: remainingUnresolved,
          ...(evidence.discovery_evidence !== undefined
            ? { discovery_evidence: evidence.discovery_evidence }
            : {}),
        }
      : null;
  return {
    ...form,
    import_evidence: nextEvidence,
  };
}

export function authoringPayloadFromUnknown(value: unknown): AuthoringPayloadForm {
  const root = asRecord(value) ?? {};
  const general = asRecord(root.general) ?? {};
  const fields = Array.isArray(root.fields) ? root.fields : [];
  const documents = Array.isArray(root.document_requirements) ? root.document_requirements : [];
  const sections = Array.isArray(root.sections) ? root.sections : [];
  const items = Array.isArray(root.items) ? root.items : [];
  const presentation = asRecord(root.presentation);
  const layout = asRecord(presentation?.layout);
  const pdf = asRecord(presentation?.pdf);

  return {
    general_name: asString(general.name),
    general_description: asString(general.description),
    fields: (() => {
      const parsed: AuthoringFieldForm[] = fields.map((row, index) => {
        const field = asRecord(row) ?? {};
        const binding: AuthoringBindingKind =
          field.binding_kind === 'CASE_SNAPSHOT' ||
          field.binding_kind === 'CASE_DYNAMIC' ||
          field.binding_kind === 'CHECKLIST_DYNAMIC'
            ? field.binding_kind
            : 'CHECKLIST_DYNAMIC';
        const snapshot = classifySnapshotPath(asString(field.snapshot_path));
        const valueType = AUTHORING_FIELD_VALUE_TYPES.includes(
          field.value_type as AuthoringFieldValueType,
        )
          ? (field.value_type as AuthoringFieldValueType)
          : 'STRING';
        const owner = AUTHORING_OWNER_CODES.includes(field.owner_code as AuthoringOwnerCode)
          ? (field.owner_code as AuthoringOwnerCode)
          : 'CONTRACTOR';
        const placement = AUTHORING_PLACEMENTS.includes(field.placement as AuthoringPlacement)
          ? (field.placement as AuthoringPlacement)
          : 'OBJECT_BLOCK';
        return {
          field_key: asString(field.field_key, `field_${index + 1}`),
          binding_kind: binding,
          label: asString(field.label),
          value_type: valueType,
          owner_code: owner,
          required_rule: parseRequiredRule(field.required_rule),
          applicability_rule: parseApplicabilityRule(field.applicability_rule),
          validation: asRecord(field.validation) ?? {},
          placement,
          ordinal: asNumber(field.ordinal, index),
          pdf_label: pdfLabelFromMetadata(field.pdf_metadata),
          semantic_key: asString(field.semantic_key),
          master_object_role: parseMasterBindingFormValue(field.master_object_role),
          master_attribute: parseMasterBindingFormValue(field.master_attribute),
          ...snapshot,
        };
      });
      const hasExplicitOrdinal = fields.some((row) => {
        const field = asRecord(row);
        return field !== null && typeof field.ordinal === 'number' && Number.isFinite(field.ordinal);
      });
      const ordered = hasExplicitOrdinal
        ? [...parsed].sort((a, b) => a.ordinal - b.ordinal || a.field_key.localeCompare(b.field_key))
        : parsed;
      return ordered.map((field, index) => ({ ...field, ordinal: index }));
    })(),
    document_requirements: documents.map((row, index) => {
      const document = asRecord(row) ?? {};
      const capture = AUTHORING_CAPTURE_MODES.includes(
        document.capture_mode as AuthoringCaptureMode,
      )
        ? (document.capture_mode as AuthoringCaptureMode)
        : 'METADATA_PREFILL';
      const metadata = Array.isArray(document.metadata_fields) ? document.metadata_fields : [];
      const verification = asRecord(document.verification_metadata);
      return {
        document_type_id: asString(document.document_type_id),
        capture_mode: capture,
        display_label: asString(document.display_label),
        subject_binding: asString(document.subject_binding, 'VEHICLE'),
        required_rule: parseRequiredRule(document.required_rule),
        applicability_rule: parseApplicabilityRule(document.applicability_rule),
        verification_note: asString(verification?.note),
        ordinal: asNumber(document.ordinal, index),
        metadata_fields: metadata.map((metaRow, metaIndex) => {
          const meta = asRecord(metaRow) ?? {};
          const metaType = AUTHORING_FIELD_VALUE_TYPES.includes(
            meta.value_type as AuthoringFieldValueType,
          )
            ? (meta.value_type as AuthoringFieldValueType)
            : 'STRING';
          return {
            field_code: asString(meta.field_code, `meta_${metaIndex + 1}`),
            label: asString(meta.label),
            value_type: metaType,
            required: asBoolean(meta.required),
            ordinal: asNumber(meta.ordinal, metaIndex),
            validation: asRecord(meta.validation) ?? { version: 1 },
          };
        }),
      };
    }),
    sections: sections.map((row, index) => {
      const section = asRecord(row) ?? {};
      return {
        section_code: asString(section.section_code, `SEC_${index + 1}`),
        ordinal: asNumber(section.ordinal, index),
        title: asString(section.title),
      };
    }),
    items: items.map((row, index) => {
      const item = asRecord(row) ?? {};
      const owner = AUTHORING_OWNER_CODES.includes(item.owner_code as AuthoringOwnerCode)
        ? (item.owner_code as AuthoringOwnerCode)
        : 'INSPECTOR';
      return {
        section_code: asString(item.section_code),
        item_code: asString(item.item_code, `ITEM_${index + 1}`),
        ordinal: asNumber(item.ordinal, index),
        label: asString(item.label),
        owner_code: owner,
        required_rule: parseRequiredRule(item.required_rule),
        applicability_rule: parseApplicabilityRule(item.applicability_rule),
        validation: parseItemValidation(item.validation),
        normative_reference: asString(item.normative_reference),
        pdf_label: pdfLabelFromMetadata(item.pdf_metadata),
      };
    }),
    presentation: {
      enabled: presentation !== null && asNumber(presentation.schema_version, 0) === 1,
      layout_title: asString(layout?.title),
      pdf_title: asString(pdf?.title),
      pdf_include_normative_references: asBoolean(pdf?.include_normative_references),
    },
    header_binding_review: parseHeaderBindingReviewForm(root.header_binding_review),
    pending_alias_actions: [],
    alias_learning_error: null,
    import_evidence: parseImportEvidence(root.import_evidence),
  };
}

function parseHeaderBindingReviewForm(value: unknown): HeaderBindingReviewForm | null {
  const root = asRecord(value);
  if (root === null || root.schema !== 'header-binding-review/v1') {
    return null;
  }
  const title = asRecord(root.title_candidate);
  const groups = Array.isArray(root.groups) ? root.groups : [];
  const candidates = Array.isArray(root.candidates) ? root.candidates : [];
  return {
    schema: 'header-binding-review/v1',
    source_sheet: asString(root.source_sheet),
    title_candidate:
      title === null
        ? null
        : {
            candidate_id: asString(title.candidate_id),
            raw_text: asString(title.raw_text),
            normalized_text: asString(title.normalized_text),
            source_row: asNumber(title.source_row, 0),
            source_cells: Array.isArray(title.source_cells)
              ? title.source_cells.map((cell) => String(cell))
              : [],
          },
    groups: groups.map((row) => {
      const group = asRecord(row) ?? {};
      return {
        candidate_id: asString(group.candidate_id),
        raw_title: asString(group.raw_title),
        normalized_title: asString(group.normalized_title),
        source_row: asNumber(group.source_row, 0),
        source_cells: Array.isArray(group.source_cells)
          ? group.source_cells.map((cell) => String(cell))
          : [],
      };
    }),
    candidates: candidates.map((row) => {
      const candidate = asRecord(row) ?? {};
      const resolution = asRecord(candidate.resolution) ?? { state: 'UNRESOLVED' };
      const confidence =
        candidate.confidence === 'HIGH' ||
        candidate.confidence === 'MEDIUM' ||
        candidate.confidence === 'NONE'
          ? candidate.confidence
          : 'NONE';
      const state =
        resolution.state === 'BOUND' ||
        resolution.state === 'CREATED_FIELD' ||
        resolution.state === 'IGNORED' ||
        resolution.state === 'UNRESOLVED'
          ? resolution.state
          : 'UNRESOLVED';
      return {
        candidate_id: asString(candidate.candidate_id),
        raw_label: asString(candidate.raw_label),
        normalized_label: asString(candidate.normalized_label),
        source_row: asNumber(candidate.source_row, 0),
        label_cells: Array.isArray(candidate.label_cells)
          ? candidate.label_cells.map((cell) => String(cell))
          : [],
        value_cells: Array.isArray(candidate.value_cells)
          ? candidate.value_cells.map((cell) => String(cell))
          : [],
        group_id:
          candidate.group_id === null || candidate.group_id === undefined
            ? null
            : String(candidate.group_id),
        group_title:
          candidate.group_title === null || candidate.group_title === undefined
            ? null
            : String(candidate.group_title),
        confidence,
        suggested_binding: asRecord(candidate.suggested_binding),
        resolution: {
          state,
          resulting_field_key:
            typeof resolution.resulting_field_key === 'string'
              ? resolution.resulting_field_key
              : undefined,
          reason: typeof resolution.reason === 'string' ? resolution.reason : undefined,
        },
      };
    }),
  };
}

function serializeCfgValFieldValidation(
  valueType: string,
  validation: Record<string, unknown>,
): Record<string, unknown> {
  if (isLegacyPreserveValidation(validation)) {
    return { ...validation };
  }
  if (isBlankValidation(validation)) {
    return defaultValidationRuleV1(valueType);
  }
  if (isGrammarV1Validation(validation)) {
    return serializeValidationRuleV1(valueType, validation);
  }
  return defaultValidationRuleV1(valueType);
}

export function serializeAuthoringPayload(form: AuthoringPayloadForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    fields: form.fields.map((field, index) => {
      const row: Record<string, unknown> = {
        field_key: field.field_key.trim(),
        binding_kind: field.binding_kind,
        label: field.label.trim(),
        value_type: field.value_type,
        owner_code: field.owner_code,
        required_rule: serializeRequiredRule(field.required_rule),
        applicability_rule: serializeApplicabilityRule(field.applicability_rule),
        validation: serializeCfgValFieldValidation(field.value_type, field.validation),
        placement: field.placement,
        // Draft-local order; backend may strip on parse but array order is preserved.
        ordinal: field.ordinal >= 0 ? field.ordinal : index,
      };
      const pdf = serializePdfMetadata(field.pdf_label);
      if (pdf !== undefined) {
        row.pdf_metadata = pdf;
      }
      if (field.binding_kind === 'CASE_SNAPSHOT') {
        row.snapshot_path = resolveSnapshotPath(field);
        row.semantic_key = null;
      } else if (field.binding_kind === 'CASE_DYNAMIC') {
        row.semantic_key = field.semantic_key.trim();
        row.snapshot_path = null;
      } else {
        row.snapshot_path = null;
        row.semantic_key = null;
      }
      const masterRole = field.master_object_role.trim();
      const masterAttr = field.master_attribute.trim();
      if (masterRole.length > 0 && masterRole !== 'NONE' && masterAttr.length > 0 && masterAttr !== 'NONE') {
        row.master_object_role = masterRole;
        row.master_attribute = masterAttr;
      } else {
        row.master_object_role = null;
        row.master_attribute = null;
      }
      return row;
    }),
    document_requirements: form.document_requirements.map((document, index) => {
      const row: Record<string, unknown> = {
        document_type_id: document.document_type_id,
        capture_mode: document.capture_mode,
        subject_binding: document.subject_binding.trim(),
        required_rule: serializeRequiredRule(document.required_rule),
        applicability_rule: serializeApplicabilityRule(document.applicability_rule),
        ordinal: document.ordinal >= 0 ? document.ordinal : index,
        metadata_fields: document.metadata_fields.map((meta, metaIndex) => ({
          field_code: meta.field_code.trim(),
          label: meta.label.trim(),
          value_type: meta.value_type,
          required: meta.required,
          ordinal: meta.ordinal >= 0 ? meta.ordinal : metaIndex,
          validation: serializeCfgValFieldValidation(meta.value_type, meta.validation),
        })),
      };
      if (document.display_label.trim().length > 0) {
        row.display_label = document.display_label.trim();
      }
      if (document.verification_note.trim().length > 0) {
        row.verification_metadata = { note: document.verification_note.trim() };
      }
      return row;
    }),
    sections: form.sections.map((section, index) => ({
      section_code: section.section_code.trim(),
      ordinal: section.ordinal >= 0 ? section.ordinal : index,
      title: section.title.trim(),
    })),
    items: form.items.map((item, index) => {
      const row: Record<string, unknown> = {
        section_code: item.section_code.trim(),
        item_code: item.item_code.trim(),
        ordinal: item.ordinal >= 0 ? item.ordinal : index,
        label: item.label.trim(),
        owner_code: item.owner_code,
        required_rule: serializeRequiredRule(item.required_rule),
        applicability_rule: serializeApplicabilityRule(item.applicability_rule),
        validation: {
          version: 1,
          type: 'ITEM_VALIDATION',
          fields: item.validation.fields.map((field) => ({
            field_key: field.field_key.trim(),
            value_type: field.value_type,
            required_when: field.required_when,
          })),
        },
      };
      if (item.normative_reference.trim().length > 0) {
        row.normative_reference = item.normative_reference.trim();
      } else {
        row.normative_reference = null;
      }
      const pdf = serializePdfMetadata(item.pdf_label);
      if (pdf !== undefined) {
        row.pdf_metadata = pdf;
      }
      return row;
    }),
  };

  if (form.general_name.trim().length > 0 || form.general_description.trim().length > 0) {
    payload.general = {
      name: form.general_name.trim(),
      description:
        form.general_description.trim().length === 0 ? null : form.general_description.trim(),
    };
  }

  if (form.presentation.enabled) {
    const layout: Record<string, unknown> = {};
    const pdf: Record<string, unknown> = {};
    if (form.presentation.layout_title.trim().length > 0) {
      layout.title = form.presentation.layout_title.trim();
    }
    if (form.presentation.pdf_title.trim().length > 0) {
      pdf.title = form.presentation.pdf_title.trim();
    }
    if (form.presentation.pdf_include_normative_references) {
      pdf.include_normative_references = true;
    }
    payload.presentation = {
      schema_version: 1,
      layout,
      pdf,
    };
  } else {
    payload.presentation = null;
  }

  if (form.header_binding_review !== null) {
    payload.header_binding_review = form.header_binding_review;
  }

  if (form.import_evidence !== null) {
    payload.import_evidence = serializeImportEvidence(form.import_evidence);
  }

  return payload;
}

export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= items.length || to < 0 || to >= items.length || from === to) {
    return [...items];
  }
  const next = [...items];
  const [row] = next.splice(from, 1);
  if (row === undefined) {
    return [...items];
  }
  next.splice(to, 0, row);
  return next;
}

export function reindexOrdinals<T extends { ordinal: number }>(items: readonly T[]): T[] {
  return items.map((item, index) => ({ ...item, ordinal: index }));
}

const BINDING_BLUEPRINT_KEYS = [
  'binding_kind',
  'value_type',
  'owner_code',
  'placement',
  'required_rule',
  'applicability_rule',
  'validation',
  'snapshot_path',
  'semantic_key',
] as const;

function deepCloneJson<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      // fall through to JSON
    }
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Same spirit as backend normalizeHeaderText: lowercase, trim, collapse whitespace. */
export function normalizeHeaderLabel(text: string): string {
  return text
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('ru-RU');
}

export function blueprintsEquivalent(
  a: Record<string, unknown> | null | undefined,
  b: Record<string, unknown> | null | undefined,
): boolean {
  if (a == null || b == null) {
    return a == null && b == null;
  }
  const pick = (source: Record<string, unknown>) => {
    const out: Record<string, unknown> = {};
    for (const key of BINDING_BLUEPRINT_KEYS) {
      out[key] = source[key] ?? null;
    }
    return out;
  };
  return JSON.stringify(pick(a)) === JSON.stringify(pick(b));
}

export function applyBindingBlueprintToField(
  field: AuthoringFieldForm,
  blueprint: Record<string, unknown>,
): AuthoringFieldForm {
  const cloned = deepCloneJson(blueprint);
  const bindingKind = cloned.binding_kind;
  const valueType = cloned.value_type;
  const ownerCode = cloned.owner_code;
  const placement = cloned.placement;
  return {
    ...field,
    binding_kind:
      bindingKind === 'CASE_SNAPSHOT' ||
      bindingKind === 'CASE_DYNAMIC' ||
      bindingKind === 'CHECKLIST_DYNAMIC'
        ? bindingKind
        : field.binding_kind,
    value_type:
      typeof valueType === 'string' &&
      (AUTHORING_FIELD_VALUE_TYPES as readonly string[]).includes(valueType)
        ? (valueType as AuthoringFieldValueType)
        : field.value_type,
    owner_code:
      ownerCode === 'CONTRACTOR' || ownerCode === 'INSPECTOR' || ownerCode === 'SYSTEM'
        ? ownerCode
        : field.owner_code,
    placement:
      placement === 'HEADER' ||
      placement === 'OBJECT_BLOCK' ||
      placement === 'RESULT_BLOCK' ||
      placement === 'SIGNATURE_BLOCK'
        ? placement
        : field.placement,
    required_rule: deepCloneJson(
      (cloned.required_rule as AuthoringRequiredRule | undefined) ?? field.required_rule,
    ),
    applicability_rule: deepCloneJson(
      (cloned.applicability_rule as AuthoringApplicabilityRule | undefined) ??
        field.applicability_rule,
    ),
    validation: deepCloneJson(
      (cloned.validation as Record<string, unknown> | undefined) ?? field.validation,
    ),
    snapshot_path:
      cloned.snapshot_path === null || cloned.snapshot_path === undefined
        ? ''
        : String(cloned.snapshot_path),
    semantic_key:
      cloned.semantic_key === null || cloned.semantic_key === undefined
        ? ''
        : String(cloned.semantic_key),
  };
}

export function fieldMatchesBlueprint(
  field: AuthoringFieldForm,
  blueprint: Record<string, unknown>,
): boolean {
  return blueprintsEquivalent(blueprintFromField(field), blueprint);
}

export function findEquivalentField(
  fields: readonly AuthoringFieldForm[],
  normalizedLabel: string,
  blueprint: Record<string, unknown>,
): AuthoringFieldForm | undefined {
  const target = normalizeHeaderLabel(normalizedLabel);
  return fields.find(
    (field) =>
      normalizeHeaderLabel(field.label) === target && fieldMatchesBlueprint(field, blueprint),
  );
}

export function blueprintFromField(field: AuthoringFieldForm): Record<string, unknown> {
  return {
    binding_kind: field.binding_kind,
    value_type: field.value_type,
    owner_code: field.owner_code,
    placement: field.placement,
    required_rule: deepCloneJson(field.required_rule),
    applicability_rule: deepCloneJson(field.applicability_rule),
    validation: deepCloneJson(field.validation),
    snapshot_path: field.snapshot_path.trim().length === 0 ? null : field.snapshot_path,
    semantic_key: field.semantic_key.trim().length === 0 ? null : field.semantic_key,
  };
}

function overlayAliasKey(normalizedLabel: unknown, normalizedGroup: unknown): string {
  const label = normalizeHeaderLabel(String(normalizedLabel ?? ''));
  const group =
    normalizedGroup === null || normalizedGroup === undefined || normalizedGroup === ''
      ? ''
      : normalizeHeaderLabel(String(normalizedGroup));
  return `${label}\0${group}`;
}

export function mergeAliasActionsIntoOverlay(
  overlay: Record<string, unknown>,
  actions: readonly HeaderAliasAction[],
): { overlay: Record<string, unknown>; changed: boolean } {
  const aliases: Record<string, unknown>[] = Array.isArray(overlay.aliases)
    ? (overlay.aliases as unknown[]).map((row) =>
        row !== null && typeof row === 'object' && !Array.isArray(row)
          ? { ...(row as Record<string, unknown>) }
          : {},
      )
    : [];
  let changed = false;

  for (const action of actions) {
    const nextAlias: Record<string, unknown> = {
      normalized_label: normalizeHeaderLabel(action.normalized_label),
      normalized_group:
        action.normalized_group === null ? null : normalizeHeaderLabel(action.normalized_group),
      status: action.status,
      binding_blueprint: deepCloneJson(action.binding_blueprint),
      raw_label_examples: [...action.raw_label_examples],
    };
    const key = overlayAliasKey(nextAlias.normalized_label, nextAlias.normalized_group);
    const index = aliases.findIndex(
      (row) => overlayAliasKey(row.normalized_label, row.normalized_group) === key,
    );
    if (index < 0) {
      aliases.push(nextAlias);
      changed = true;
      continue;
    }
    const existing = aliases[index]!;
    if (action.status === 'SUGGESTED' && existing.status === 'TRUSTED') {
      continue;
    }
    if (JSON.stringify(existing) !== JSON.stringify(nextAlias)) {
      aliases[index] = nextAlias;
      changed = true;
    }
  }

  return {
    overlay: {
      schema: 'header-binding-overlay/v1',
      aliases,
    },
    changed,
  };
}

export function confidenceLabel(confidence: HeaderConfidence): string {
  switch (confidence) {
    case 'HIGH':
      return 'Точное совпадение';
    case 'MEDIUM':
      return 'Предложение';
    case 'NONE':
      return 'Требует решения';
  }
}

export function resolutionLabel(state: HeaderResolutionState): string {
  switch (state) {
    case 'UNRESOLVED':
      return 'Требует решения';
    case 'BOUND':
      return 'Привязано';
    case 'CREATED_FIELD':
      return 'Создано новое поле';
    case 'IGNORED':
      return 'Игнорируется';
  }
}

export function aliasCompatibilityKey(
  normalizedLabel: string,
  normalizedGroup: string | null,
): string {
  return overlayAliasKey(normalizedLabel, normalizedGroup);
}

/** Remove SUGGESTED/TRUSTED pending actions for one compatibility key. */
export function clearPendingAliasActionsForKey(
  actions: readonly HeaderAliasAction[],
  normalizedLabel: string,
  normalizedGroup: string | null,
): HeaderAliasAction[] {
  const label = normalizeHeaderLabel(normalizedLabel);
  const group =
    normalizedGroup === null || normalizedGroup === undefined || normalizedGroup === ''
      ? null
      : normalizeHeaderLabel(normalizedGroup);
  return actions.filter((action) => {
    const actionLabel = normalizeHeaderLabel(action.normalized_label);
    const actionGroup =
      action.normalized_group === null ? null : normalizeHeaderLabel(action.normalized_group);
    return !(actionLabel === label && actionGroup === group);
  });
}

/**
 * After group rename: rekey pending actions from old normalized group to new.
 * Collisions: TRUSTED wins over SUGGESTED; last TRUSTED/last SUGGESTED otherwise.
 */
export function rekeyPendingAliasActionsForGroup(
  actions: readonly HeaderAliasAction[],
  oldNormalizedGroup: string,
  newNormalizedGroup: string,
): HeaderAliasAction[] {
  const oldGroup = normalizeHeaderLabel(oldNormalizedGroup);
  const newGroup = normalizeHeaderLabel(newNormalizedGroup);
  if (oldGroup === newGroup) {
    return [...actions];
  }
  const remapped = actions.map((action) => {
    const actionGroup =
      action.normalized_group === null ? null : normalizeHeaderLabel(action.normalized_group);
    if (actionGroup !== oldGroup) {
      return action;
    }
    return {
      ...action,
      normalized_group: newGroup,
      normalized_label: normalizeHeaderLabel(action.normalized_label),
    };
  });
  const byKey = new Map<string, HeaderAliasAction>();
  for (const action of remapped) {
    const key = overlayAliasKey(action.normalized_label, action.normalized_group);
    const existing = byKey.get(key);
    if (existing === undefined) {
      byKey.set(key, action);
      continue;
    }
    if (existing.status === 'TRUSTED' && action.status !== 'TRUSTED') {
      continue;
    }
    if (action.status === 'TRUSTED' && existing.status !== 'TRUSTED') {
      byKey.set(key, action);
      continue;
    }
    byKey.set(key, action);
  }
  return [...byKey.values()];
}
